import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_ACTION = "https://readdy.ai/api/form/db8hlgbk13ra8mig8ru0";

type Status = "idle" | "submitting" | "success" | "error";

const HIGHLIGHTS = [
  {
    icon: "ri-stack-line",
    title: "丰富内容",
    desc: "覆盖电影、电视剧、综艺三大类别，精选 90+ 部原创示范内容。",
  },
  {
    icon: "ri-filter-3-line",
    title: "精准筛选",
    desc: "支持地区、年份、类型与排序多维筛选，快速定位想看的内容。",
  },
  {
    icon: "ri-search-eye-line",
    title: "全文搜索",
    desc: "按视频标题与主演关键词搜索，几秒内找到相关结果。",
  },
  {
    icon: "ri-heart-3-line",
    title: "本地收藏",
    desc: "收藏喜欢的视频，状态保存在本地，刷新页面依然保留。",
  },
];

export default function About() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  usePageMeta({
    title: "关于我们 - 密桃视频 - 高清视频内容平台",
    description:
      "了解密桃视频的平台定位、内容特色与使用说明，并通过意见反馈表单与我们联系。",
    keywords: "密桃视频,关于我们,视频平台,意见反馈,联系方式",
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("website_alt") as string | null)?.trim() ?? "";
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value !== "string") return;
      if (key === "website_alt") return;
      const trimmed = value.trim();
      if (trimmed === "") return;
      params.append(key, trimmed);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试");
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-6 md:px-6 lg:px-8 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">关于我们</span>
      </nav>

      <section className="rounded-lg border border-background-200/70 bg-background-100/70 p-6 md:p-10">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
          <i className="ri-play-circle-line"></i>
          关于密桃视频
        </span>
        <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          高清视频内容平台，丰富多彩的视频资源
        </h1>
        <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
          密桃视频是一个专注于高清视频内容聚合与展示的平台，面向广大喜爱电影、电视剧和综艺节目的用户。
          我们以清晰的分类、精准的筛选和丰富的详情信息，帮助你更高效地浏览与发现优质内容。
          网站目前已整理电影、电视剧、综艺三大类共 90 余部原创示范内容，并根据热度与更新时间持续呈现最新动态。
        </p>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
          需要特别说明的是，密桃视频目前仅提供内容展示与浏览功能，<strong>不提供在线播放服务</strong>，
          页面中的“播放”按钮为静态演示，敬请知晓。
        </p>
      </section>

      <section className="mt-10" aria-label="平台特色">
        <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-600">
            <i className="ri-lightbulb-line"></i>
          </span>
          平台特色
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {HIGHLIGHTS.map((item) => (
            <div
              key={item.title}
              className="rounded-lg border border-background-200/70 bg-background-50 p-5"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.2fr]" aria-label="联系我们">
        <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <i className="ri-customer-service-2-line text-primary-500"></i>
            联系我们
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-700">
            如果你对密桃视频有任何建议、内容反馈或合作意向，欢迎通过留言表单与我们联系。
            我们会认真阅读每一条留言，并尽快回复。
          </p>
          <ul className="mt-5 space-y-4 text-sm">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className="ri-mail-line"></i>
              </span>
              <span>
                <span className="block text-foreground-950">支持邮箱</span>
                <span className="text-foreground-600">support@mitaovideo.example.com</span>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className="ri-time-line"></i>
              </span>
              <span>
                <span className="block text-foreground-950">回复时间</span>
                <span className="text-foreground-600">工作日 9:00 - 18:00，一般 1-2 个工作日内处理</span>
              </span>
            </li>
          </ul>
        </div>

        <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <i className="ri-chat-3-line text-primary-500"></i>
            意见反馈留言
          </h2>

          {status === "success" ? (
            <div className="mt-5 flex flex-col items-center justify-center rounded-lg bg-primary-50 py-12 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-background-50">
                <i className="ri-check-line text-2xl"></i>
              </span>
              <p className="mt-4 text-base font-medium text-foreground-950">留言已提交，感谢你的反馈</p>
              <p className="mt-2 text-sm text-foreground-600">我们会尽快查看并处理你的留言。</p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-5 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                再写一条
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-readdy-form id="about-feedback-form" className="mt-5 space-y-4">
              <div>
                <label htmlFor="feedback-name" className="mb-1.5 block text-sm text-foreground-800">
                  你的称呼
                </label>
                <input
                  id="feedback-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的称呼"
                  className="h-11 w-full rounded-md border border-background-200/80 bg-background-100/60 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                />
              </div>
              <div>
                <label htmlFor="feedback-email" className="mb-1.5 block text-sm text-foreground-800">
                  联系邮箱
                </label>
                <input
                  id="feedback-email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收回复，例如 name@example.com"
                  className="h-11 w-full rounded-md border border-background-200/80 bg-background-100/60 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                />
              </div>
              <div>
                <label htmlFor="feedback-message" className="mb-1.5 block text-sm text-foreground-800">
                  留言内容
                </label>
                <textarea
                  id="feedback-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="写下你的建议、反馈或想了解更多的问题（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-200/80 bg-background-100/60 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                />
              </div>

              <input
                type="text"
                name="website_alt"
                className="form-guard-alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "error" && formError && (
                <p className="flex items-center gap-2 rounded-md bg-accent-50 px-3 py-2 text-sm text-accent-700">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap sm:w-auto"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                {status === "submitting" ? "提交中…" : "提交留言"}
              </button>
            </form>
          )}
        </div>
      </section>

      <section className="mt-10 rounded-lg border border-background-200/70 bg-secondary-50 p-6 md:p-8" aria-label="版权声明">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
          <i className="ri-copyright-line text-secondary-600"></i>
          版权声明与免责声明
        </h2>
        <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-700">
          <p>
            本站所展示的全部视频资料、封面图片、标题与文案均为<strong>原创示范数据</strong>，
            仅用于产品演示与学习交流，不指向任何真实作品。
          </p>
          <p>
            密桃视频不提供在线播放服务，页面内所有播放按钮均为静态演示，不会加载或播放实际视频内容。
          </p>
          <p>
            若本站内容无意中侵犯了你的合法权益，请通过上方联系方式与我们取得联系，我们将及时核实并处理。
          </p>
        </div>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </section>
    </div>
  );
}